import React, { useState } from 'react';
import { Navigate, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Shield, Lock, User } from 'lucide-react';

const AdminLogin = () => {
  const { isAuthenticated, isLoading, login } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    username: 'aerosy',
    totpCode: '',
  });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Redirect if already authenticated
  if (isAuthenticated && !isLoading) {
    return <Navigate to="/admin" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.totpCode || formData.totpCode.length !== 6) {
      setError('Please enter a valid 6-digit TOTP code');
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await login(formData.username, formData.totpCode);
      
      if (result.success) {
        // Navigate to admin dashboard
        navigate('/admin', { replace: true });
      } else {
        setError(result.message || 'Invalid credentials');
        setFormData(prev => ({ ...prev, totpCode: '' }));
      }
    } catch (err) {
      setError('Login failed. Please try again.');
      setFormData(prev => ({ ...prev, totpCode: '' }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTotpChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setFormData(prev => ({ ...prev, totpCode: value }));
    setError(''); // Clear error on input
  };

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-cyan-500/10 rounded-2xl border border-cyan-500/20 mb-4">
            <Shield className="w-8 h-8 text-cyan-500" />
          </div>
          <h1 className="text-3xl font-bold mb-2">Admin Login</h1>
          <p className="text-neutral-400">
            Enter your TOTP code to access the dashboard
          </p>
        </div>

        {/* Login Form */}
        <div className="bg-neutral-900/50 border border-neutral-800 rounded-2xl p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Username Field */}
            <div className="space-y-2">
              <label htmlFor="username" className="block text-sm font-medium text-neutral-300">
                Username
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500" />
                <Input
                  id="username"
                  type="text"
                  value={formData.username}
                  readOnly
                  className="pl-10 bg-neutral-800 border-neutral-700 text-neutral-400 cursor-not-allowed"
                  data-testid="admin-login-username"
                />
              </div>
            </div>

            {/* TOTP Code Field */}
            <div className="space-y-2">
              <label htmlFor="totp" className="block text-sm font-medium text-neutral-300">
                TOTP Code
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500" />
                <Input
                  id="totp"
                  type="text"
                  inputMode="numeric"
                  pattern="\d*"
                  maxLength="6"
                  value={formData.totpCode}
                  onChange={handleTotpChange}
                  placeholder="000000"
                  className="pl-10 bg-neutral-800 border-neutral-700 text-white text-center text-xl tracking-widest font-mono"
                  autoComplete="off"
                  autoFocus
                  disabled={isSubmitting}
                  data-testid="admin-login-totp"
                />
              </div>
              <p className="text-xs text-neutral-500">
                Enter the 6-digit code from your authenticator app
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div 
                className="bg-red-500/10 border border-red-500/50 rounded-lg p-3 text-sm text-red-200"
                data-testid="admin-login-error"
              >
                {error}
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isSubmitting || formData.totpCode.length !== 6}
              className="w-full bg-cyan-500 hover:bg-cyan-600 text-black font-medium py-3 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="admin-login-submit"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-solid border-black border-r-transparent"></div>
                  Verifying...
                </span>
              ) : (
                'Login'
              )}
            </Button>
          </form>

          {/* Setup Link */}
          <div className="mt-6 pt-6 border-t border-neutral-800 text-center">
            <p className="text-sm text-neutral-400 mb-2">
              First time setup?
            </p>
            <Link
              to="/admin/setup"
              className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors font-medium"
              data-testid="admin-login-setup-link"
            >
              Configure TOTP Authenticator →
            </Link>
          </div>
        </div>

        {/* Back to Home */}
        <div className="mt-6 text-center">
          <Link
            to="/"
            className="text-sm text-neutral-500 hover:text-neutral-400 transition-colors"
          >
            ← Back to Portfolio
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
